import {Box, Image, Integer} from 'platform/foundation';

import {ReactNode} from 'react';

import {isNil} from 'ramda';

import {useGetMakeModelWithMakeQuery} from '@dms/api/sales';

import {Nullish, TestIdProps, useBrandImageUrl} from 'shared/web';

import {CATALOGUE_MAKE_KEY_PREFIX} from '../../constants/CATALOGUE_MAKE_KEY_PREFIX';
import {LOGO_VEHICLE_TYPE} from '../../constants/LOGO_VEHICLE_TYPE';

interface MakeLogoProps extends TestIdProps {
  /** Catalogue make key, e.g. MAKE_VOLKSWAGEN. */
  make: string | Nullish;
  /** Omit to fill the available space in a shortcut tile. */
  size?: Integer;
  label?: string;
  fallback?: ReactNode;
}

/** Alpha owns the artwork; the caller provides text when a logo is unavailable. */
export function MakeLogo(props: MakeLogoProps) {
  const make = props.make ?? '';
  // All visible makes share one cached list request, without fetching each make's models.
  const {currentData} = useGetMakeModelWithMakeQuery(
    {vehicleType: LOGO_VEHICLE_TYPE, logoUrl: true},
    {skip: !make.startsWith(CATALOGUE_MAKE_KEY_PREFIX)}
  );
  const catalogueLogo = currentData?.find((item) => item.make === make)?.logo_url_2x?.trim();
  const {logoUrl, onLogoError} = useBrandImageUrl(catalogueLogo);

  if (isNil(logoUrl)) {
    return props.fallback ?? null;
  }

  // Keep the image and its wrapper within the tile's content area, including padding.
  return (
    <Box flexShrink={0} width={props.size ?? '100%'} height={props.size ?? '100%'}>
      <Image
        key={logoUrl}
        src={logoUrl}
        alt={props.label ?? ''}
        width="100%"
        height="100%"
        fit="contain"
        onError={onLogoError}
        data-testid={props['data-testid']}
      />
    </Box>
  );
}
